import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import {
  useGetTrainerSitesQuery,
  useRemoveTrainerSlotMutation,
  useUpsertTrainerSlotMutation,
} from "../../../features/sites/sitesApiSlice";
import { useGetTrainerListQuery } from "../../../features/users/usersApiSlice";
import { selectCurrentUserData } from "../../../features/auth/authSlice";
import useAuth from "../../../hooks/useAuth";
import {
  addDays,
  formatDateLabel,
  formatUserLabel,
  getRangeLabel,
  getWeekDateValues,
  getMonthCells,
  getMonthDateValues,
  normalizeAssignments,
  toDateValue,
} from "../trainerUtils";
import {
  buildAdminTrainerOptions,
  buildRangeSummary,
  buildRegularBookingPreview,
  buildSlotAssigneeOptions,
  buildSlotsByDate,
  buildTrainerSites,
  buildTrainerSummary,
  getDayOverview,
} from "../trainerPageData";

const DEFAULT_REGULAR_BOOKING_CONFIG = {
  isOpen: false,
  durationType: "indefinite",
  months: 3,
};

export default function useTrainerPageController() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const today = useMemo(() => new Date(), []);
  const { isRealAdmin, isRealSuperAdmin } = useAuth();
  const userData = useSelector(selectCurrentUserData);
  const currentUserId = userData?.id || userData?._id || "";
  const currentUserName = formatUserLabel(userData);
  const canManageBookedTrainerSlots = isRealAdmin || isRealSuperAdmin;
  const canViewAdminSummary = canManageBookedTrainerSlots;

  const {
    data: trainerSitesData,
    isLoading,
    isError,
  } = useGetTrainerSitesQuery();
  const { data: trainerListData = [], isFetching: isTrainerListLoading } =
    useGetTrainerListQuery(undefined, {
      skip: !canViewAdminSummary,
    });
  const [upsertTrainerSlot, { isLoading: isUpsertingSlot }] =
    useUpsertTrainerSlotMutation();
  const [removeTrainerSlot, { isLoading: isRemovingSlot }] =
    useRemoveTrainerSlotMutation();

  const [pageView, setPageView] = useState("plan");
  const [selectedAdminTrainerId, setSelectedAdminTrainerId] = useState("");
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [siteFilterInitialized, setSiteFilterInitialized] = useState(false);
  const [calendarView, setCalendarView] = useState(isMobile ? "week" : "month");
  const [calendarAnchor, setCalendarAnchor] = useState(
    new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12),
  );
  const [activeSlot, setActiveSlot] = useState(null);
  const [slotError, setSlotError] = useState("");
  const [regularBookingConfig, setRegularBookingConfig] = useState(
    DEFAULT_REGULAR_BOOKING_CONFIG,
  );
  const [siteOverrides, setSiteOverrides] = useState({});
  const [summaryError, setSummaryError] = useState("");
  const [summaryDeletingEntryKey, setSummaryDeletingEntryKey] = useState("");
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    const dayOfWeek = (today.getDay() + 6) % 7;
    return dayOfWeek;
  });
  const [monthSelection, setMonthSelection] = useState(() => ({
    monthKey: `${today.getFullYear()}-${today.getMonth()}`,
    dateValue: toDateValue(today),
  }));
  const [mobileFocusCue, setMobileFocusCue] = useState(null);

  const mobileRevealTimeoutRef = useRef(null);
  const mobileWeekRevealRefs = useRef({});
  const mobileMonthDetailsRef = useRef(null);

  const trainerSites = useMemo(
    () => buildTrainerSites(trainerSitesData, siteOverrides),
    [siteOverrides, trainerSitesData],
  );

  const allSiteIds = useMemo(
    () => trainerSites.map((site) => site?._id).filter(Boolean),
    [trainerSites],
  );

  useEffect(() => {
    if (!trainerSites.length) {
      if (selectedSiteIds.length) setSelectedSiteIds([]);
      if (siteFilterInitialized) setSiteFilterInitialized(false);
      return;
    }

    if (!siteFilterInitialized) {
      setSelectedSiteIds(allSiteIds);
      setSiteFilterInitialized(true);
      return;
    }

    const validSelectedSiteIds = selectedSiteIds.filter((siteId) =>
      allSiteIds.includes(siteId),
    );

    if (validSelectedSiteIds.length !== selectedSiteIds.length) {
      setSelectedSiteIds(validSelectedSiteIds);
    }
  }, [
    allSiteIds,
    selectedSiteIds,
    siteFilterInitialized,
    trainerSites.length,
  ]);

  const activeSites = useMemo(
    () => trainerSites.filter((site) => selectedSiteIds.includes(site?._id)),
    [selectedSiteIds, trainerSites],
  );

  useEffect(() => {
    if (activeSlot && !selectedSiteIds.includes(activeSlot.siteId)) {
      setActiveSlot(null);
      setSlotError("");
    }
  }, [activeSlot, selectedSiteIds]);

  useEffect(() => {
    setRegularBookingConfig(DEFAULT_REGULAR_BOOKING_CONFIG);
  }, [activeSlot?.date, activeSlot?.sessionKey, activeSlot?.siteId]);

  useEffect(
    () => () => {
      if (typeof window !== "undefined" && mobileRevealTimeoutRef.current) {
        window.clearTimeout(mobileRevealTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!mobileFocusCue || typeof window === "undefined") return undefined;
    const timeoutId = window.setTimeout(() => setMobileFocusCue(null), 1100);
    return () => window.clearTimeout(timeoutId);
  }, [mobileFocusCue]);

  const rangeLabel = useMemo(
    () => getRangeLabel(calendarView, calendarAnchor),
    [calendarAnchor, calendarView],
  );
  const visibleDateValues = useMemo(
    () =>
      calendarView === "month"
        ? getMonthDateValues(calendarAnchor)
        : getWeekDateValues(calendarAnchor),
    [calendarAnchor, calendarView],
  );
  const monthCells = useMemo(
    () => (calendarView === "month" ? getMonthCells(calendarAnchor) : []),
    [calendarAnchor, calendarView],
  );
  const monthDateValues = useMemo(
    () => getMonthDateValues(calendarAnchor),
    [calendarAnchor],
  );

  const slotsByDate = useMemo(
    () =>
      buildSlotsByDate({
        activeSites,
        currentUserId,
        visibleDateValues,
      }),
    [activeSites, currentUserId, visibleDateValues],
  );

  const rangeSummary = useMemo(
    () => buildRangeSummary(visibleDateValues, slotsByDate),
    [slotsByDate, visibleDateValues],
  );
  const hasSlots = rangeSummary.total > 0;
  const hasSessions = useMemo(
    () => activeSites.some((site) => site.sessions.length > 0),
    [activeSites],
  );
  const isSlotMutating = isUpsertingSlot || isRemovingSlot;

  const summary = useMemo(
    () => buildTrainerSummary(trainerSites, currentUserId, calendarAnchor),
    [calendarAnchor, currentUserId, trainerSites],
  );
  const adminTrainerOptions = useMemo(
    () =>
      canViewAdminSummary
        ? buildAdminTrainerOptions(trainerSites, trainerListData)
        : [],
    [canViewAdminSummary, trainerListData, trainerSites],
  );
  const selectedAdminTrainer = useMemo(
    () =>
      adminTrainerOptions.find((trainer) => trainer.id === selectedAdminTrainerId) ||
      null,
    [adminTrainerOptions, selectedAdminTrainerId],
  );
  const adminSummary = useMemo(
    () =>
      buildTrainerSummary(
        selectedAdminTrainerId ? trainerSites : [],
        selectedAdminTrainerId || "__no-admin-trainer__",
        calendarAnchor,
      ),
    [calendarAnchor, selectedAdminTrainerId, trainerSites],
  );
  const adminSlotAssigneeOptions = useMemo(
    () =>
      canManageBookedTrainerSlots
        ? buildSlotAssigneeOptions(trainerListData, activeSlot?.siteId)
        : [],
    [activeSlot?.siteId, canManageBookedTrainerSlots, trainerListData],
  );

  const patchSiteAssignments = useCallback((siteId, nextAssignments) => {
    if (!siteId) return;
    setSiteOverrides((current) => ({
      ...current,
      [siteId]: normalizeAssignments(nextAssignments),
    }));
  }, []);

  const handleToggleSite = useCallback(
    (siteId) => {
      setSelectedSiteIds((current) =>
        current.includes(siteId)
          ? current.filter((id) => id !== siteId)
          : allSiteIds.filter((id) => id === siteId || current.includes(id)),
      );
      setActiveSlot(null);
      setSlotError("");
    },
    [allSiteIds],
  );

  const handleSelectAllSites = useCallback(() => {
    setSelectedSiteIds(allSiteIds);
    setActiveSlot(null);
    setSlotError("");
  }, [allSiteIds]);

  const handleNavigate = useCallback(
    (direction) => {
      setCalendarAnchor((currentAnchor) =>
        calendarView === "month"
          ? new Date(
              currentAnchor.getFullYear(),
              currentAnchor.getMonth() + direction,
              1,
              12,
            )
          : addDays(currentAnchor, direction * 7),
      );
    },
    [calendarView],
  );

  const handleSummaryNavigate = useCallback((direction) => {
    setSummaryError("");
    setCalendarAnchor(
      (currentAnchor) =>
        new Date(
          currentAnchor.getFullYear(),
          currentAnchor.getMonth() + direction,
          1,
          12,
        ),
    );
  }, []);

  const handleToday = useCallback(() => {
    setSummaryError("");
    setCalendarAnchor(
      new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12),
    );
    setSelectedDayIndex((today.getDay() + 6) % 7);
    setMonthSelection({
      monthKey: `${today.getFullYear()}-${today.getMonth()}`,
      dateValue: toDateValue(today),
    });
  }, [today]);

  const handleSummaryDeleteEntry = useCallback(
    async (entry, options = {}) => {
      if (!entry?.siteId || !entry?.sessionKey) return;

      setSummaryError("");
      setSummaryDeletingEntryKey(entry.entryKey || "");

      try {
        const response = await removeTrainerSlot({
          id: entry.siteId,
          sessionKey: entry.sessionKey,
          ...(entry.occurrenceType === "regular"
            ? {
                removeRegular: true,
                date: entry.date || "",
                startDate: entry.startDate || "",
                endDate: entry.endDate || "",
                deleteScope: options.deleteScope || "all",
              }
            : {
                date: entry.date,
              }),
        }).unwrap();

        patchSiteAssignments(entry.siteId, response?.trainerAssignments);
      } catch (error) {
        setSummaryError(
          error?.data?.message || "Die Buchung konnte nicht gelöscht werden.",
        );
      } finally {
        setSummaryDeletingEntryKey("");
      }
    },
    [patchSiteAssignments, removeTrainerSlot],
  );

  const openSlotDialog = useCallback((slot) => {
    setActiveSlot(slot);
    setSlotError("");
  }, []);

  const closeSlotDialog = useCallback(() => {
    if (isSlotMutating) return;
    setActiveSlot(null);
    setSlotError("");
  }, [isSlotMutating]);

  const regularBookingPreview = useMemo(
    () => buildRegularBookingPreview(activeSlot, regularBookingConfig),
    [activeSlot, regularBookingConfig],
  );

  const openRegularBookingStep = useCallback(() => {
    setRegularBookingConfig((current) => ({ ...current, isOpen: true }));
    setSlotError("");
  }, []);

  const closeRegularBookingStep = useCallback(() => {
    if (isSlotMutating) return;
    setRegularBookingConfig((current) => ({ ...current, isOpen: false }));
    setSlotError("");
  }, [isSlotMutating]);

  const handleRegularBookingDurationChange = useCallback((durationType) => {
    setRegularBookingConfig((current) => ({ ...current, durationType }));
  }, []);

  const handleRegularBookingMonthsChange = useCallback((months) => {
    setRegularBookingConfig((current) => ({
      ...current,
      months: Number(months) || 1,
    }));
  }, []);

  const handleTakeSlot = useCallback(
    async (mode = "single", options = {}) => {
      if (!activeSlot?.siteId || !currentUserId) return;

      const selectedTrainerId = String(options?.trainerId || "").trim();
      const selectedTrainerName = String(options?.trainerName || "").trim();
      const effectiveTrainerId =
        canManageBookedTrainerSlots && selectedTrainerId
          ? selectedTrainerId
          : currentUserId;
      const effectiveTrainerName =
        canManageBookedTrainerSlots && selectedTrainerName
          ? selectedTrainerName
          : currentUserName;

      setSlotError("");

      try {
        const payload = {
          id: activeSlot.siteId,
          sessionKey: activeSlot.sessionKey,
          day: activeSlot.session.day,
          title: activeSlot.session.title,
          start: activeSlot.session.start,
          end: activeSlot.session.end,
          date: activeSlot.date,
          trainerId: effectiveTrainerId,
          trainerName: effectiveTrainerName,
          assignmentMode: mode,
        };

        if (mode === "regular") {
          payload.startDate = options.startDate || activeSlot.date;
          if (options.endDate) payload.endDate = options.endDate;
        }

        const response = await upsertTrainerSlot(payload).unwrap();
        patchSiteAssignments(activeSlot.siteId, response?.trainerAssignments);
        setActiveSlot(null);
      } catch (error) {
        setSlotError(
          error?.data?.message || "Der Slot konnte nicht übernommen werden.",
        );
      }
    },
    [
      activeSlot,
      canManageBookedTrainerSlots,
      currentUserId,
      currentUserName,
      patchSiteAssignments,
      upsertTrainerSlot,
    ],
  );

  const handleReleaseSlot = useCallback(async (options = {}) => {
    const canReleaseActiveSlot =
      activeSlot?.status === "mine" ||
      (canManageBookedTrainerSlots && activeSlot?.status === "occupied");
    if (!activeSlot?.siteId || !canReleaseActiveSlot) return;

    setSlotError("");

    try {
      const response = await removeTrainerSlot({
        id: activeSlot.siteId,
        sessionKey: activeSlot.sessionKey,
        ...(activeSlot.assignmentMode === "regular"
          ? {
              date: activeSlot.date,
              removeRegular: true,
              startDate: activeSlot.assignment?.startDate || "",
              endDate: activeSlot.assignment?.endDate || "",
              deleteScope: options.deleteScope || "all",
            }
          : {
              date: activeSlot.date,
            }),
      }).unwrap();

      patchSiteAssignments(activeSlot.siteId, response?.trainerAssignments);
      setActiveSlot(null);
    } catch (error) {
      setSlotError(
        error?.data?.message || "Die Buchung konnte nicht gelöscht werden.",
      );
    }
  }, [
    activeSlot,
    canManageBookedTrainerSlots,
    patchSiteAssignments,
    removeTrainerSlot,
  ]);

  const weekDateValues = useMemo(
    () => getWeekDateValues(calendarAnchor),
    [calendarAnchor],
  );
  const todayValue = toDateValue(today);
  const calendarMonthKey = useMemo(
    () => `${calendarAnchor.getFullYear()}-${calendarAnchor.getMonth()}`,
    [calendarAnchor],
  );
  const monthDefaultDateValue = useMemo(() => {
    if (!monthDateValues.length) return null;
    if (monthDateValues.includes(todayValue)) return todayValue;
    return (
      monthDateValues.find((dateValue) => (slotsByDate.get(dateValue) || []).length > 0) ||
      monthDateValues[0]
    );
  }, [monthDateValues, slotsByDate, todayValue]);
  const selectedMonthDateValue =
    monthSelection.monthKey === calendarMonthKey
      ? monthSelection.dateValue || (!isMobile ? monthDefaultDateValue : null)
      : monthDefaultDateValue;
  const selectedMonthSlots = useMemo(
    () =>
      selectedMonthDateValue
        ? slotsByDate.get(selectedMonthDateValue) || []
        : [],
    [selectedMonthDateValue, slotsByDate],
  );
  const selectedMonthDateLabel = useMemo(
    () =>
      selectedMonthDateValue
        ? formatDateLabel(selectedMonthDateValue, {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : "",
    [selectedMonthDateValue],
  );
  const selectedMonthSiteCount = useMemo(
    () =>
      new Set(selectedMonthSlots.map((slot) => slot.siteId).filter(Boolean))
        .size,
    [selectedMonthSlots],
  );
  const selectedMonthOverview = useMemo(
    () => getDayOverview(selectedMonthSlots),
    [selectedMonthSlots],
  );

  const scheduleMobileReveal = useCallback(
    (getElement, focusKey, delay = 180) => {
      if (!isMobile || typeof window === "undefined") return;

      if (mobileRevealTimeoutRef.current) {
        window.clearTimeout(mobileRevealTimeoutRef.current);
      }

      mobileRevealTimeoutRef.current = window.setTimeout(() => {
        const element = getElement();
        if (!element) return;

        const reduceMotion =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        element.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "start",
        });
        setMobileFocusCue(focusKey);
      }, delay);
    },
    [isMobile],
  );

  const handleMobileWeekDaySelection = useCallback(
    (index, dateValue) => {
      const nextIndex = selectedDayIndex === index ? null : index;
      setSelectedDayIndex(nextIndex);

      if (nextIndex !== null) {
        scheduleMobileReveal(
          () => mobileWeekRevealRefs.current[dateValue],
          `week-${dateValue}`,
          240,
        );
      }
    },
    [scheduleMobileReveal, selectedDayIndex],
  );

  const handleMonthDateSelection = useCallback(
    (dateValue) => {
      if (!dateValue) return;

      const nextDateValue =
        isMobile &&
        monthSelection.monthKey === calendarMonthKey &&
        monthSelection.dateValue === dateValue
          ? null
          : dateValue;

      setMonthSelection({
        monthKey: calendarMonthKey,
        dateValue: nextDateValue,
      });

      if (nextDateValue) {
        scheduleMobileReveal(
          () => mobileMonthDetailsRef.current,
          `month-${nextDateValue}`,
          140,
        );
      }
    },
    [
      calendarMonthKey,
      isMobile,
      monthSelection.dateValue,
      monthSelection.monthKey,
      scheduleMobileReveal,
    ],
  );

  const toggleCalendarView = useCallback(() => {
    setCalendarView((currentView) =>
      currentView === "week" ? "month" : "week",
    );
  }, []);

  const handleChangePageView = useCallback(
    (nextView) => {
      if (nextView === "admin-summary" && !canViewAdminSummary) {
        setPageView("plan");
        return;
      }

      setPageView(nextView);
    },
    [canViewAdminSummary],
  );

  const showPlanView = useCallback(() => {
    setPageView("plan");
  }, []);

  useEffect(() => {
    if (!canViewAdminSummary) {
      if (pageView === "admin-summary") {
        setPageView("plan");
      }
      if (selectedAdminTrainerId) {
        setSelectedAdminTrainerId("");
      }
      return;
    }

    if (!adminTrainerOptions.length) {
      if (selectedAdminTrainerId) {
        setSelectedAdminTrainerId("");
      }
      return;
    }

    const selectedTrainerExists = adminTrainerOptions.some(
      (trainer) => trainer.id === selectedAdminTrainerId,
    );

    if (!selectedTrainerExists) {
      setSelectedAdminTrainerId(adminTrainerOptions[0].id);
    }
  }, [
    adminTrainerOptions,
    canViewAdminSummary,
    pageView,
    selectedAdminTrainerId,
  ]);

  return {
    isLoading,
    isError,
    trainerSites,
    isMobile,
    canViewAdminSummary,
    page: {
      view: pageView,
      onChangeView: handleChangePageView,
    },
    summary: {
      data: summary,
      error: summaryError,
      deletingEntryKey: summaryDeletingEntryKey,
      onNavigate: handleSummaryNavigate,
      onToday: handleToday,
      onDeleteEntry: handleSummaryDeleteEntry,
      onShowPlan: showPlanView,
    },
    adminSummary: {
      data: adminSummary,
      error: summaryError,
      deletingEntryKey: summaryDeletingEntryKey,
      isTrainerListLoading,
      trainerOptions: adminTrainerOptions,
      selectedTrainerId: selectedAdminTrainerId,
      selectedTrainer: selectedAdminTrainer,
      onSelectTrainer: setSelectedAdminTrainerId,
      onNavigate: handleSummaryNavigate,
      onToday: handleToday,
      onDeleteEntry: handleSummaryDeleteEntry,
      onShowPlan: showPlanView,
    },
    plan: {
      trainerSites,
      allSiteIds,
      selectedSiteIds,
      activeSites,
      hasSessions,
      hasSlots,
      rangeSummary,
      calendarView,
      rangeLabel,
      weekDateValues,
      todayValue,
      selectedDayIndex,
      slotsByDate,
      monthCells,
      selectedMonthDateValue,
      selectedMonthSlots,
      selectedMonthDateLabel,
      selectedMonthSiteCount,
      selectedMonthOverview,
      mobileFocusCue,
      mobileWeekRevealRefs,
      mobileMonthDetailsRef,
      onToggleSite: handleToggleSite,
      onSelectAllSites: handleSelectAllSites,
      onNavigate: handleNavigate,
      onToday: handleToday,
      onToggleCalendarView: toggleCalendarView,
      onSelectWeekDay: handleMobileWeekDaySelection,
      onSelectMonthDate: handleMonthDateSelection,
      onOpenSlot: openSlotDialog,
    },
    dialog: {
      activeSlot,
      isMobile,
      slotError,
      isSlotMutating,
      canAssignSlots: Boolean(currentUserId),
      canAssignForOthers: canManageBookedTrainerSlots,
      canManageBookedSlots: canManageBookedTrainerSlots,
      currentUserId,
      adminAssigneeOptions: adminSlotAssigneeOptions,
      isAdminAssigneeLoading:
        canManageBookedTrainerSlots && isTrainerListLoading,
      regularBookingConfig,
      regularBookingPreview,
      onClose: closeSlotDialog,
      onOpenRegularBooking: openRegularBookingStep,
      onCloseRegularBooking: closeRegularBookingStep,
      onChangeRegularBookingDuration: handleRegularBookingDurationChange,
      onChangeRegularBookingMonths: handleRegularBookingMonthsChange,
      onTakeSlot: handleTakeSlot,
      onReleaseSlot: handleReleaseSlot,
    },
  };
}
